import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Combobox,
  Icon,
  Loader,
  Notice,
  Toast
} from '@deque/cauldron-react';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AdminFeatureFlag,
  type SSOConfig,
  createEnterpriseFeatureFlagOverride,
  deleteEnterpriseFeatureFlagOverride,
  updateEnterpriseFeatureFlagOverride
} from '~/common/api-client';
import type { AuthUser } from '~/common/contexts/auth';
import { useGlobalToast } from '~/common/contexts/globalToast';
import type { ErrorOrNull, OrNull } from '~/typings/utils';
import AddEnterpriseFeatureFlagOverrideModal from '../components/AddEnterpriseFeatureFlagOverrideModal';
import type { ComboboxOptionType } from '../components/configuration/MultiSelectSetting';
import EnterpriseFeatureFlagsOverridesTable, {
  type OnUpdateOverrideArgs
} from '../components/EnterpriseFeatureFlagsOverridesTable';
import useEnterpriseFeatureFlagOverrides from '../hooks/useEnterpriseFeatureFlagOverrides';
import useOverriddenEnterpriseFeatureIds from '../hooks/useOverriddenEnterpriseFeatureIds';
import styles from './EnterpriseFeatureFlagOverrides.css';

const ITEMS_PER_PAGE = 25;

interface RemoveOverrideAlertProps {
  overrideId: string;
  onClose: () => void;
  onRemove: (overrideId: string) => Promise<void>;
}

const RemoveOverrideAlert = ({
  overrideId,
  onClose,
  onRemove
}: RemoveOverrideAlertProps) => {
  const { t } = useTranslation();
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    try {
      setRemoving(true);
      await onRemove(overrideId);
    } finally {
      setRemoving(false);
      onClose();
    }
  };

  return (
    <Alert
      heading={t('Remove Override')}
      show
      onClose={onClose}
      variant="warning"
    >
      <AlertContent>
        {t(
          'Are you sure you want to remove this override? This action cannot be undone.'
        )}
      </AlertContent>
      <AlertActions>
        <Button disabled={removing} variant="error" onClick={handleRemove}>
          {t('Remove')}
        </Button>
        <Button disabled={removing} variant="secondary" onClick={onClose}>
          {t('Cancel')}
        </Button>
      </AlertActions>
    </Alert>
  );
};

export interface EnterpriseFeatureFlagOverridesContainerProps {
  user: AuthUser;
  narrow: boolean;
  features: AdminFeatureFlag[];
  featuresLoading: boolean;
  featuresError: ErrorOrNull;
  ssoConfig: OrNull<SSOConfig>;
  ssoConfigLoading: boolean;
}

const EnterpriseFeatureFlagOverridesContainer: React.FC<
  EnterpriseFeatureFlagOverridesContainerProps
> = ({
  user,
  narrow,
  features,
  featuresError,
  featuresLoading,
  ssoConfig,
  ssoConfigLoading
}) => {
  const { t } = useTranslation();
  const token = user.token ?? '';
  const { setContents: setToastContents } = useGlobalToast();

  const [filteredFeatureIds, setFilteredFeatureIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [showAddModal, setShowAddModal] = useState(false);
  const [comboboxKey, setComboboxKey] = useState(0);
  const [removingOverrideId, setRemovingOverrideId] =
    useState<OrNull<string>>(null);

  const {
    overrides,
    total,
    refresh: refreshEnterpriseFeatureFlagOverrides,
    error: enterpriseFeatureFlagOverridesError,
    loading: enterpriseFeatureFlagOverridesLoading
  } = useEnterpriseFeatureFlagOverrides({
    token,
    feature_ids: filteredFeatureIds,
    page,
    limit: ITEMS_PER_PAGE,
    sort_by: sortBy,
    sort_direction: sortDirection
  });

  const {
    featureIds: overriddenFeatureIds,
    refresh: refreshOverriddenFeatureIds,
    error: overriddenEnterpriseFeatureIdsError,
    loading: overriddenEnterpriseFeatureIdsLoading
  } = useOverriddenEnterpriseFeatureIds(token);

  const loading =
    enterpriseFeatureFlagOverridesLoading ||
    overriddenEnterpriseFeatureIdsLoading ||
    ssoConfigLoading;

  const loaderRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (loading && element) {
        element.focus();
      }
    },
    [loading]
  );

  const error =
    enterpriseFeatureFlagOverridesError || overriddenEnterpriseFeatureIdsError;

  const resetCombobox = () => {
    // NOTE: Cauldron's Combobox stores selectedValues, initialized
    // once from the value prop on mount. Prop changes only sync inputValue (the
    // text field), leaving selectedValues stale.
    // As a workaround, we manually remount it.
    setComboboxKey(k => k + 1);
  };

  const handleSort = (column: string) => {
    if (column === sortBy) {
      setSortDirection(d => (d === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(column);
      setSortDirection('ASC');
    }
    setPage(1);
  };

  const handleFeatureFilterChange = (selectedIds: string[]) => {
    setFilteredFeatureIds(selectedIds);
    setPage(1);
  };

  const refreshTable = () => {
    setFilteredFeatureIds([]);
    setPage(1);
    refreshOverriddenFeatureIds();
    refreshEnterpriseFeatureFlagOverrides();
    resetCombobox();
  };

  const handleAdd = async (
    featureId: string,
    enterpriseIds: string[],
    state: boolean
  ): Promise<void> => {
    await createEnterpriseFeatureFlagOverride({
      token,
      feature_id: featureId,
      enterprise_ids: enterpriseIds,
      state
    });
    refreshTable();
    setToastContents(t('Override added successfully.'), 'confirmation');
  };

  const handleOverrideUpdate = async (args: OnUpdateOverrideArgs) => {
    try {
      await updateEnterpriseFeatureFlagOverride({
        ...args,
        token: user?.token ?? ''
      });
      refreshEnterpriseFeatureFlagOverrides();
      setToastContents(t('Override updated successfully.'), 'confirmation');
    } catch {
      setToastContents(
        t('Failed to update override. Please try again.'),
        'error'
      );
    }
  };

  const handleRemove = async (overrideId: string): Promise<void> => {
    try {
      await deleteEnterpriseFeatureFlagOverride({ token, overrideId });
      const nextPage = overrides.length === 1 && page > 1 ? page - 1 : page;
      setPage(nextPage);
      refreshEnterpriseFeatureFlagOverrides();
      setToastContents(t('Override removed successfully.'), 'confirmation');
    } catch {
      setToastContents(
        t('Failed to remove override. Please try again.'),
        'error'
      );
    }
  };

  const handleCloseRemoveAlert = () => {
    setRemovingOverrideId(null);
  };

  const handleCloseOverrideAlert = () => {
    setShowAddModal(false);
  };

  const handleRemoveOverrideAction = (id: string) => {
    setRemovingOverrideId(id);
  };

  const pageHandlers = useMemo(() => {
    const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
    return {
      first: () => setPage(1),
      previous: () => setPage(p => p - 1),
      next: () => setPage(p => p + 1),
      last: () => setPage(totalPages)
    };
  }, [total]);

  const featureFilterOptions: ComboboxOptionType[] = useMemo(
    () =>
      overriddenFeatureIds.map(id => ({
        key: id,
        label: id,
        value: id
      })),
    [overriddenFeatureIds]
  );

  const isAnyFeatureFilterSelected = filteredFeatureIds.length > 0;
  const isAnyFeatureFilterOptions = featureFilterOptions.length > 0;

  const canAddOverride = !featuresLoading && !featuresError;

  return (
    <div className={styles.container}>
      <Toast type="error" show={!!featuresError}>
        {t('Failed to load features: {{error}}', {
          error: featuresError?.message
        })}
      </Toast>
      <Notice title={t('Caution')} type="caution">
        <p>
          {t(
            'Enterprise overrides take precedence over global feature flag defaults for the selected enterprise.'
          )}
        </p>
      </Notice>

      <div className={styles.controlsContainer}>
        <Combobox
          label={t('Filter by feature flag')}
          name="feature-filter"
          multiselect
          options={featureFilterOptions}
          key={comboboxKey}
          disabled={!isAnyFeatureFilterOptions}
          placeholder={
            !isAnyFeatureFilterSelected ? t('Select feature') : undefined
          }
          className={styles.featureFilter}
          onSelectionChange={({ value: selected }) =>
            handleFeatureFilterChange(selected as string[])
          }
        />
        <Button
          className={styles.addOverrideButton}
          disabled={!canAddOverride}
          variant="primary"
          onClick={() => setShowAddModal(true)}
        >
          <Icon type="plus" />
          {t('Add Override')}
        </Button>
      </div>
      <div className={styles.tableContainer}>
        {loading && (
          <Loader
            label={t('Loading overrides')}
            tabIndex={-1}
            ref={loaderRef}
          />
        )}

        {error && (
          <p>
            <strong>{t('Unable to load overrides:')}</strong> {error.message}
          </p>
        )}

        {!loading && !error && (
          <div
            className={classNames(styles.overridesBody, {
              [styles.overridesBodyMobile]: narrow
            })}
          >
            <EnterpriseFeatureFlagsOverridesTable
              overrides={overrides}
              total={total}
              currentPage={page}
              itemsPerPage={ITEMS_PER_PAGE}
              narrow={narrow}
              sortBy={sortBy}
              sortDirection={sortDirection}
              ssoConfig={ssoConfig}
              onSort={handleSort}
              onRemove={handleRemoveOverrideAction}
              onNextPageClick={pageHandlers.next}
              onPreviousPageClick={pageHandlers.previous}
              onFirstPageClick={pageHandlers.first}
              onLastPageClick={pageHandlers.last}
              onUpdate={handleOverrideUpdate}
            />
          </div>
        )}
      </div>
      <AddEnterpriseFeatureFlagOverrideModal
        show={showAddModal}
        features={features}
        onClose={handleCloseOverrideAlert}
        onSave={handleAdd}
      />

      {removingOverrideId && (
        <RemoveOverrideAlert
          overrideId={removingOverrideId}
          onClose={handleCloseRemoveAlert}
          onRemove={handleRemove}
        />
      )}
    </div>
  );
};

export default EnterpriseFeatureFlagOverridesContainer;
